import React from "react";
import { sort } from "radash";
import { Reducers } from "@core";
import { useGetParams } from "@hooks";
import { renderTooltip } from "@utils";
import { Icon } from "@nubeteck/icons";
import { EventUtils } from "@nubeteck/utils";
import { ChangeLogDialog } from "@components";
import { Skeleton } from "primereact/skeleton";
import { TabView, TabPanel } from "primereact/tabview";
import Chart, { ChartWrapperOptions } from "react-google-charts";
import { Card, Description, useHeaderActions } from "@nubeteck/prime";

import { useAccountingQuery } from "../../hooks";
import { ExchangeRatesTab } from "../../components";

const CurrencyDetailsPage = () => {
  const currencyId = useGetParams("id");
  const { dispatch: dispatchHeaderActions } = useHeaderActions();

  const currency = useAccountingQuery("Currency", "getById", currencyId, {
    enabled: !!currencyId,
  });

  const { data: exchangeRate } = useAccountingQuery(
    "ExchangeRates",
    "getByDateRange",
    {
      fechaDesde: "",
      fechaHasta: "",
      monedaId: currencyId,
    },
    { enabled: !!currencyId },
  );

  const [changeLogState, changeLogDispatch] = React.useReducer(
    Reducers.DialogReducer,
    { id: 0, open: false },
  );

  React.useEffect(() => {
    if (exchangeRate) {
      dispatchHeaderActions({
        type: "SET_ACTIONS",
        payload: [
          <React.Fragment key="Logs">
            {renderTooltip(
              "Historial de cambios",
              "changelog-peu-poa",
              "right",
            )}
            <Icon
              key=""
              name="book"
              className="changelog-peu-poa align-self-center text-primary mr-1"
              onClick={EventUtils.callEvent(changeLogDispatch, {
                type: "OPEN_DIALOG",
                payload: currencyId,
              })}
            />
          </React.Fragment>,
        ],
      });
    }
    return () => dispatchHeaderActions({ type: "CLEAR_ACTIONS" });
  }, [currencyId, dispatchHeaderActions, exchangeRate]);

  const value = React.useMemo(() => {
    return (
      exchangeRate?.map((rate) => ({
        ...rate,
        Fecha: new Date(rate.Fecha),
      })) ?? []
    );
  }, [exchangeRate]);

  const chartData = React.useMemo(() => {
    const sortedData = sort(value, (item) => item.Fecha.getTime());
    return [
      ["Fecha", "Precio de compra", "Precio de venta"],
      ...sortedData.map((rate) => [
        rate.Fecha,
        rate.PrecioCompra,
        rate.PrecioVenta,
      ]),
    ];
  }, [value]);

  const chartOptions: ChartWrapperOptions["options"] = {
    curveType: "function",
    legend: { position: "bottom" },
    vAxis: {
      title: "Precio",
    },
    hAxis: {
      title: "Fecha",
      format: "dd/MM/yyyy",
    },
  };

  const tabs = [
    {
      title: "Tasas de cambio",
      content: <ExchangeRatesTab />,
    },
  ];

  const items = [
    {
      label: "ISO",
      value: currency.data?.NombreISO ?? "",
    },
    {
      label: "Símbolo",
      value: currency.data?.Simbolo ?? "",
    },
    {
      label: "Locale",
      value: currency.data?.Locale ?? "",
    },
    {
      label: "Es principal",
      value: currency.data?.EsPrincipal ? "Sí" : "No",
    },
  ];

  if (currency.isPending) {
    return (
      <>
        <Skeleton height="5rem" className="mb-2" />
        <Skeleton height="20rem" />
      </>
    );
  }

  return (
    <>
      <div className="flex flex-column gap-2 w-full">
        <Description columns={2} items={items} title="Información general" />
        {!currency.data?.EsPrincipal && (
          <>
            <TabView scrollable panelContainerClassName="p-0">
              {tabs.map((tab) => (
                <TabPanel key={tab.title} header={tab.title}>
                  {tab.content}
                </TabPanel>
              ))}
            </TabView>
            {chartData.length > 1 && (
              <Card>
                <div className="w-full h-full flex flex-column justify-content-center align-items-center">
                  <span className="font-bold text-lg text-800">Histórico</span>
                  <Chart
                    width="100%"
                    height="100%"
                    data={chartData}
                    chartType="LineChart"
                    options={chartOptions}
                  />
                </div>
              </Card>
            )}
          </>
        )}
      </div>
      <ChangeLogDialog
        entityName="monedaid"
        entityId={changeLogState.id}
        visible={changeLogState.open}
        onHide={EventUtils.callEvent(changeLogDispatch, {
          type: "CLOSE_DIALOG",
        })}
      />
    </>
  );
};

export default CurrencyDetailsPage;
